<template>
  <v-container
    fluid
    class="container-bg rounded elevation-3">
    <v-row justify-md="space-between">
      <v-col
        cols="12"
        md="4">
        <div class="d-flex flex-row align-center">
          <span class="text-h5 font-weight-bold">
            Finished Goods Orders
          </span>
          <v-btn
            class="mx-1"
            color="success"
            icon
            :loading="exporting"
            :disabled="loading || itemSelected.length === 0"
            @click="onExport">
            <v-icon>
              mdi-microsoft-excel
            </v-icon>
          </v-btn>
          <span v-if="itemSelected.length > 0">
            {{ itemSelected.length }} item(s)
          </span>
        </div>
      </v-col>
      <v-spacer />
      <v-col
        cols="12"
        md="2">
        <v-select
          v-model="query.sortBy"
          :items="sortByOptions"
          item-text="text"
          item-value="value"
          label="เรียงจาก"
          outlined
          dense
          hide-details>
        </v-select>
      </v-col>
      <v-col
        cols="12"
        md="2">
        <v-select
          v-model="query.sortOrder"
          :items="sortOrderOptions"
          item-text="text"
          item-value="value"
          label="เรียงลำดับ"
          outlined
          dense
          hide-details>
        </v-select>
      </v-col>
      <v-col
        cols="12"
        md="auto">
        <div class="d-flex flex-row justify-end align-center">
          <search-box
            v-model="query.search"
            @on-search="getSearchItems()" />
          <v-btn
            color="success"
            :disabled="loading"
            to="/purchases/orders/create?type=FG"
            class="ml-2 mb-1">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-data-table
          v-model="itemSelected"
          :headers="headers"
          :items="items"
          :items-per-page="query.limit"
          :loading="loading"
          show-select
          item-key="orderNo"
          hide-default-footer>
          <template #[`item.bcSync`]="{ item }">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-icon
                  v-if="item.bcSync && item.bcSync.sync && item.bcSync.bcLog"
                  v-bind="attrs"
                  color="success"
                  v-on="on">
                  mdi-check-circle-outline
                </v-icon>
                <v-icon
                  v-else
                  v-bind="attrs"
                  color="error"
                  v-on="on">
                  mdi-sync-alert
                </v-icon>
              </template>
              <span v-if="item.bcSync && item.bcSync.updatedAt">
                Last sync date {{ item.bcSync.updatedAt | dateTimeUserFormat() }}
              </span>
              <span v-else>
                Not sync yet
              </span>
            </v-tooltip>
          </template>
          <template #[`item.orderNo`]="{ item }">
            {{ item.documentDate | dateUserFormat() }} <br>
            {{ item.orderNo }}
          </template>
          <template #[`item.vendor`]="{ item }">
            {{ `${item.vendorNumber} ${item.vendorName}` }}
          </template>
          <template #[`item.locationCode`]="{ item }">
            {{ item.locationCode }}
          </template>
          <template #[`item.state`]="{ item }">
            <v-chip
              small
              label
              :color="stateColor(item.state)"
              text-color="white">
              {{ mapState(item.state) }}
            </v-chip>
          </template>
          <template #[`item.received`]="{ item }">
            <span v-if="!item.receivedQty && !item.amount">
              ยังไม่รับสินค้า
            </span>
            <span
              v-else
              :style="item.receivedQty !== item.amount ? 'color: red;' : ''">
              {{ `${item.receivedQty} / ${item.amount}` }}
            </span>
          </template>
          <template #[`item.subscribe`]="{ item }">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  :color="isSubscribed(item) ? 'secondary' : ''"
                  small
                  icon
                  :loading="subLoadingId === item.id"
                  v-on="on"
                  @click="toggleSubscribe(item)">
                  <v-icon small>
                    {{ isSubscribed(item) ? 'mdi-bell' : 'mdi-bell-outline' }}
                  </v-icon>
                </v-btn>
              </template>
              <span>
                {{ isSubscribed(item) ? 'ยกเลิกติดตาม' : 'ติดตามเอกสารนี้' }}
              </span>
            </v-tooltip>
          </template>
          <template
            #[`item.action`]="{ item }">
            <v-row class="flex-nowrap justify-center">
              <v-btn
                color="secondary"
                small
                icon
                target="_blank"
                :to="`/purchases/orders/${item.id}/pdf`">
                <v-icon small>
                  mdi-magnify
                </v-icon>
              </v-btn>
              <v-btn
                color="secondary"
                small
                icon
                :to="`/purchases/orders/${item.id}?type=FG`">
                <v-icon small>
                  mdi-square-edit-outline
                </v-icon>
              </v-btn>
              <v-btn
                color="secondary"
                small
                icon
                :to="`/purchases/orders/create?type=FG&duplicate=${item.id}`">
                <v-icon small>
                  mdi-content-copy
                </v-icon>
              </v-btn>
            </v-row>
          </template>
        </v-data-table>
      </v-col>
      <v-col cols="12">
        <v-pagination
          v-model="query.page"
          :length="pageCount"
          :disabled="loading"
          total-visible="7"
          color="secondary" />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { ExportToCsv } from 'export-to-csv'
import SearchBox from '@/components/SearchBox.vue'
import PurchaseOrdersProvider from '@/resources/PurchaseOrdersProvider'
import ProductProvider from '@/resources/ProductProvider'
import NotificationProvider from '@/resources/NotificationProvider'

const PurchaseOrdersService = new PurchaseOrdersProvider()
const productService = new ProductProvider()
const NotificationService = new NotificationProvider()

export default {
  components: {
    SearchBox
  },
  data () {
    return {
      headers: [
        {
          text: '',
          value: 'bcSync',
          align: 'center',
          sortable: false
        },
        {
          text: 'รหัส',
          value: 'orderNo',
          align: 'center',
          width: '120'
        },
        {
          text: 'สถานะ',
          value: 'state',
          align: 'center'
        },
        {
          text: 'คลังที่รับเข้า',
          value: 'locationCode'
        },
        {
          text: 'คอลเลคชั่น',
          value: 'gwCollection',
          width: '200',
          sortable: false
        },
        {
          text: 'ผู้ขาย',
          value: 'vendor.name',
          sortable: false
        },
        {
          text: 'จำนวนที่รับ',
          value: 'received',
          sortable: false,
          align: 'center'
        },
        {
          text: 'ติดตาม',
          value: 'subscribe',
          align: 'center',
          sortable: false
        },
        {
          text: 'ตัวเลือก',
          value: 'action',
          align: 'center',
          sortable: false
        }
      ],
      itemSelected: [],
      items: [],
      sortByOptions: [
        {
          text: 'รหัส',
          value: 'orderNo'
        },
        {
          text: 'วันที่สร้าง',
          value: 'createdAt'
        },
        {
          text: 'วันที่เอกสาร',
          value: 'documentDate'
        }
      ],
      sortOrderOptions: [
        {
          text: 'A-Z',
          value: 'asc'
        },
        {
          text: 'Z-A',
          value: 'desc'
        }
      ],
      query: {
        page: 1,
        limit: 10,
        search: '',
        sortBy: 'orderNo',
        sortOrder: 'desc',
        skusCode: []
      },
      pageCount: 0,
      loading: false,
      exporting: false,
      subscribedOrderNos: [],
      subLoadingId: null
    }
  },
  watch: {
    'query.page': {
      handler () {
        this.getItems()
      },
      deep: true
    },
    'query.sortBy': {
      handler () {
        this.getItems(true)
      },
      deep: true
    },
    'query.sortOrder': {
      handler () {
        this.getItems(true)
      },
      deep: true
    }
  },
  async mounted () {
    if (this.$route.query.productId) {
      await this.getSkusByProductId()
    }
    this.getItems(true)
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setModal: 'Components/setModal'
    }),
    onConfirmApprove () {
      this.setModal({
        value: true,
        title: 'Approve',
        message: 'Do you want to approve this purchase order',
        confirmText: 'Sure',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.approve()
      })
    },
    async getSkusByProductId () {
      try {
        this.loading = true
        const { data } = await productService.getProductById(this.$route.query.productId)
        this.query.skusCode = data?.skuModels || []
      } catch (error) {
        console.error('getSkuByProduct', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async approve () {
      try {
        this.loading = true

        await Promise.all(this.itemSelected.map((item) => PurchaseOrdersService.approveItem(item.id)))

        this.getItems()
      } catch (error) {
        console.error('removeItem', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getItems (isClear = false) {
      try {
        this.loading = true
        if (isClear) {
          this.query.page = 1
          this.items = []
        }

        const { data } = await PurchaseOrdersService.getItems({ ...this.query, type: 'FG' })
        this.items = data.results.map(this.transformItemData)
        this.subscribedOrderNos = this.items
          .filter((i) => i.isSubscribed)
          .map((i) => i.orderNo)
        this.pageCount = data.pages
      } catch (error) {
        console.error('getItems', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getSearchItems () {
      this.query.skusCode = []
      this.$router.replace('')
      this.getItems(true)
    },

    transformItemData (i) {
      return {
        ...i,
        gwCollection: [...new Set(i.purchaseOrderLines.map((o) => o.gwCollection))].filter(Boolean).join(', '),
        amount: i.purchaseOrderLines.reduce((sum, o) => sum + o.quantity, 0),
        receivedQty: i.purchaseOrderLines.reduce((sum, o) => sum + (o?.receivedQty || 0), 0)
      }
    },

    confirmRemoveItem (item) {
      this.setModal({
        value: true,
        title: 'Remove Item',
        message: `Do you want to remove ${item.orderNo}?`,
        confirmText: 'Remove',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.removeItem(item.id)
      })
    },
    async removeItem (id) {
      try {
        this.loading = true

        await PurchaseOrdersService.deleteItem(id)

        this.getItems(true)
      } catch (error) {
        console.error('removeItem', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    isSubscribed (item) {
      return this.subscribedOrderNos.includes(item.orderNo)
    },
    async toggleSubscribe (item) {
      try {
        this.subLoadingId = item.id
        const { data } = await NotificationService.subscribePo(item.id)
        const active = data?.status === 'active'
        const idx = this.subscribedOrderNos.indexOf(item.orderNo)
        if (active && idx === -1) {
          this.subscribedOrderNos.push(item.orderNo)
        } else if (!active && idx !== -1) {
          this.subscribedOrderNos.splice(idx, 1)
        }
        this.setSnackbar({
          value: true,
          message: active ? `ติดตาม ${item.orderNo} แล้ว` : `ยกเลิกติดตาม ${item.orderNo} แล้ว`,
          type: 'success'
        })
      } catch (error) {
        console.error('toggleSubscribe', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.subLoadingId = null
      }
    },
    mapState (state) {
      switch (state) {
        case 'pending': return 'รอการอนุมัติ'
        case 'approved': return 'อนุมัติแล้ว'
        case 'received': return 'รับเข้าบางส่วน'
        case 'completed': return 'รับสำเร็จ'
        case 'canceled': return 'ยกเลิก'
        default: return state
      }
    },
    stateColor (state) {
      switch (state) {
        case 'pending': return 'warning'
        case 'approved': return 'success'
        case 'received': return 'info'
        case 'completed': return 'teal darken-1'
        case 'canceled': return 'error'
        default: return 'grey'
      }
    },
    async onExport () {
      try {
        this.exporting = true

        const { data } = await PurchaseOrdersService.getItems({
          page: 1,
          limit: 999999,
          sortBy: this.query.sortBy,
          sortOrder: this.query.sortOrder,
          type: 'FG',
          orderNo: this.itemSelected.map((v) => v.orderNo)
        })

        const mapped = data.results.map((item) => ({
          'No': item.orderNo,
          'Document Date': item.documentDate,
          'State': item.state,
          'Collection': [...new Set(item.purchaseOrderLines.map((line) => line.gwCollection))].join(', '),
          'Vendor Id': item.vendor.vendorNo,
          'Vendor Name': item.vendor.name,
          'จำนวนที่รับ': item.purchaseOrderLines.reduce((acc, line) => acc + (line?.receivedQty || 0), 0),
          'จำนวนของ PO': item.purchaseOrderLines.reduce((acc, line) => acc + (line?.quantity || 0), 0),
          'Expected Received Date': [...new Set(item.purchaseOrderLines.map((line) => line.expectedReceiptDate))].join(', ')
        }))

        const options = {
          filename: `purchase-order-fg-${this.$dayjs().format('YYYY-MM-DD')}`,
          showLabels: true,
          useKeysAsHeaders: true
        }

        const csvExporter = new ExportToCsv(options)
        csvExporter.generateCsv(mapped)
      } catch (error) {
        console.error('onExport', error)
        this.setSnackbar({
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped>
.container-bg {
  background-color: white;
}
</style>
